import { useEffect, useState } from "react";
import ButtonIconH from "./ButtonIconH";
import InputData from "./InputData";
import DataTable from "react-data-table-component";
import CheckBox from "./CheckBox";
import InputIconSearchPanelMultiSelection from "./InputIconSearchPanelMultiSelection";
import Tag from "./Tag";
import { focusElement } from "../js/utils";
import { useBrands } from "../hooks/useBrands";

export function ProductSalesData({
  productFamilyId,
  productFamilyName,
  list,
  columns,
  onProductChange,
  supplierId,
  tabStartIndex,
}) {
  const [productList, setProductList] = useState(list);
  const [selectedItems, setSelectedItems] = useState([]);
  const [newPVP, setNewPVP] = useState("");
  const [newRecPVP, setNewRecPVP] = useState("");
  const [brandList] = useBrands(productFamilyId, -1, "");
  const [selectedBrandList, setSelectedBrandList] = useState([]);

  function handleSelectAll() {
    setSelectedItems((prev) => [
      ...prev,
      ...productList
        .filter(({ idProductoAlbaran }) => !prev.includes(idProductoAlbaran))
        .map(({ idProductoAlbaran }) => idProductoAlbaran),
    ]);
  }

  function handleCleanValues() {
    setNewPVP("");
    setNewRecPVP("");
    setSelectedBrandList([]);
    focusElement(tabStartIndex);
  }

  function handleCleanSelected() {
    setSelectedItems([]);
  }

  function handleCheckboxChange(productId) {
    setSelectedItems((prevSelected) =>
      prevSelected.includes(productId)
        ? prevSelected.filter((id) => id !== productId)
        : [...prevSelected, productId],
    );
  }

  function handleNewPVP(e) {
    setNewPVP(e);
  }

  function handleNewRecPVP(e) {
    setNewRecPVP(e);
  }

  function applyToAll() {
    setProductList((prevList) =>
      prevList.map((e) => {
        const brandIsIncluded =
          selectedBrandList.length === 0 || selectedBrandList.includes(e.marca);

        if (brandIsIncluded) {
          return {
            ...e,
            pvpRecomendado: newRecPVP !== "" ? newRecPVP : e.pvpRecomendado,
            pvp: newPVP !== "" ? newPVP : e.pvp,
          };
        }

        return e;
      }),
    );
  }

  function applyToSelection() {
    if (selectedItems.length > 0) {
      setProductList((prevList) =>
        prevList.map((e) => {
          const isSelected = selectedItems.includes(e.idProductoAlbaran);
          const brandIsIncluded =
            selectedBrandList.length === 0 ||
            selectedBrandList.includes(e.marca);

          if (isSelected && brandIsIncluded) {
            return {
              ...e,
              pvp: newPVP !== "" ? newPVP : e.pvp,
              pvpRecomendado: newRecPVP !== "" ? newRecPVP : e.pvpRecomendado,
            };
          }

          return e;
        }),
      );
    }
  }

  function validateSelectedProducts() {
    setProductList((prevList) =>
      prevList.map((product) =>
        selectedItems.includes(product.idProductoAlbaran)
          ? { ...product, validadoDatosVenta: true }
          : product,
      ),
    );
    setSelectedItems([]);
  }

  function handleValueChange(productId, field, newValue) {
    setProductList((prevList) =>
      prevList.map((e) => {
        if (e.idProductoAlbaran === productId) {
          const updatedProduct = { ...e, [field]: newValue };

          return {
            ...updatedProduct,
          };
        }
        return e;
      }),
    );
  }

  const isValidInput = (value) => {
    const regex = /^\d*\.?\d*$/; // Restricción a números y un punto.
    return regex.test(value);
  };

  const columnsWithInputs = columns.map((col) => {
    if (col.name === "PVP") {
      return {
        ...col,
        cell: (row, rowIndex) => (
          <InputData
            size={0}
            type="type-float2"
            tabIndex={tabStartIndex + 3 + rowIndex * 2 + 1}
            input={row.pvp}
            onChange={(e) => {
              if (isValidInput(e)) {
                handleValueChange(row.idProductoAlbaran, "pvp", e);
              }
            }}
            onBlur={(e) => {
              const value = e === "" ? "0" : e;
              handleValueChange(row.idProductoAlbaran, "pvp", value);
            }}
          />
        ),
      };
    }

    if (col.name === "PVP Recomendado") {
      return {
        ...col,
        cell: (row, rowIndex) => (
          <InputData
            size={0}
            type="type-float2"
            tabIndex={tabStartIndex + 3 + rowIndex * 2}
            input={row.pvpRecomendado}
            onChange={(e) => {
              if (isValidInput(e)) {
                handleValueChange(row.idProductoAlbaran, "pvpRecomendado", e);
              }
            }}
          />
        ),
      };
    }

    if (col.name === "") {
      return {
        ...col,
        selector: (row) => (
          <CheckBox
            active={selectedItems.includes(row.idProductoAlbaran)}
            onClick={() => handleCheckboxChange(row.idProductoAlbaran)}
          />
        ),
      };
    }

    return col;
  });

  useEffect(() => {
    onProductChange(productFamilyId, productList);
  }, productList);

  useEffect(() => {
    productList.filter((item) => {
      if (item.idProductoAlbaran === "summary") return true;
      if (selectedBrandList.length === 0) return true;
      return selectedBrandList.includes(item.marca);
    });
  }, [selectedBrandList, productList]);

  return (
    <>
      {list.length > 0 && (
        <div className="delivery-note-table-info-container">
          <div className="opt-table-title">
            {productFamilyName} ({list.length})
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              marginBottom: 10,
            }}
          >
            <button
              className="cell-label"
              onClick={handleCleanValues}
              tabIndex={-1}
            >
              Limpiar valores
            </button>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: 10,
              padding: "0px 0 20px 0",
            }}
          >
            <div className="data-field">
              <span className="label">Añadir PVP recomendado</span>
              <InputData
                tabIndex={tabStartIndex}
                type="type-float2"
                size={1}
                input={newRecPVP}
                onChange={handleNewRecPVP}
              ></InputData>
            </div>
            <div className="data-field">
              <span className="label">Añadir PVP</span>
              <InputData
                tabIndex={tabStartIndex + 1}
                type="type-float2"
                size={1}
                input={newPVP}
                onChange={handleNewPVP}
              ></InputData>
            </div>
            <div className="data-field" style={{ flex: 1 }}>
              <span className="label">Marca</span>
              <InputIconSearchPanelMultiSelection
                size={0}
                tabIndex={tabStartIndex + 2}
                type={"type-alphanum-ext"}
                options={brandList.filter((brand) => productList.some((p) => p.marca === brand))}
                selectedOptions={selectedBrandList}
                onChange={(e) => {
                  setSelectedBrandList(e);
                }}
              ></InputIconSearchPanelMultiSelection>
            </div>
            <ButtonIconH
              text={"Aplicar a todos"}
              small={true}
              onClick={applyToAll}
            ></ButtonIconH>
            <ButtonIconH
              disabled={selectedItems.length === 0}
              text={"Aplicar a selección"}
              small={true}
              onClick={applyToSelection}
            ></ButtonIconH>
            <ButtonIconH
              disabled={selectedItems.length === 0}
              text={"Validar"}
              small={true}
              onClick={validateSelectedProducts}
            ></ButtonIconH>
          </div>
          {selectedBrandList.length > 0 && (
            <span style={{ fontWeight: "500", marginBottom: 10, fontSize: 13 }}>
              Marcas seleccionadas: {selectedBrandList.length}
            </span>
          )}
          <div className="tag-content" style={{ maxHeight: "max-content" }}>
            {selectedBrandList.map((e, i) => {
              return (
                <Tag
                  text={e}
                  key={i}
                  tagId={i}
                  onClick={(e) => {
                    setSelectedBrandList((prevBrands) =>
                      prevBrands.filter((brand) => brand !== e),
                    );
                  }}
                ></Tag>
              );
            })}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "5px",
              justifyContent: "flex-end",
              paddingBottom: 10,
            }}
          >
            <button
              className="cell-label"
              onClick={handleSelectAll}
              tabIndex={-1}
            >
              Seleccionar todos
            </button>
            |
            <button
              className="cell-label"
              onClick={handleCleanSelected}
              tabIndex={-1}
            >
              Limpiar selección
            </button>
          </div>
          <div className="store-products-table delivery-note-table">
            <DataTable
              columns={columnsWithInputs}
              data={productList.filter(
                (e) =>
                  selectedBrandList.length === 0 ||
                  selectedBrandList.includes(e.marca),
              )}
              className="custom-datatable non-interactive"
              noDataComponent="No hay productos disponibles."
              selectableRows={false}
              customInputsTabIndexStart={tabStartIndex + 3}
              conditionalRowStyles={[
                {
                  when: (row) => row.validadoDatosVenta === true,
                  classNames: ["validated-product"],
                },
              ]}
            />
          </div>
        </div>
      )}
    </>
  );
}
